import { constrainRectToAspect, normalizeRect, PORTRAIT_9_16, type CropRect } from './frame-processing';

/** Edge / corner grabbed on the crop box (compass: n = top, e = right). */
export type CropHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export type CropHit = CropHandle | 'move' | null;

type Point = { x: number; y: number };

/**
 * What a pointer at `p` grabs: an edge or corner (within tolerance), the inside (move), or nothing
 * (draw a new box). Coordinates and tolerances are normalized to the frame.
 */
export function cropHitTest(rect: CropRect | null, p: Point, tolX: number, tolY: number): CropHit {
  if (!rect) return null;
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;
  const withinX = p.x >= rect.x - tolX && p.x <= right + tolX;
  const withinY = p.y >= rect.y - tolY && p.y <= bottom + tolY;
  if (!withinX || !withinY) return null;
  const v = Math.abs(p.y - rect.y) <= tolY ? 'n' : Math.abs(p.y - bottom) <= tolY ? 's' : '';
  const h = Math.abs(p.x - rect.x) <= tolX ? 'w' : Math.abs(p.x - right) <= tolX ? 'e' : '';
  return v || h ? ((v + h) as CropHandle) : 'move';
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Translate the box, keeping it fully inside the frame. */
export function moveRect(rect: CropRect, dx: number, dy: number): CropRect {
  return {
    x: clamp(rect.x + dx, 0, 1 - rect.w),
    y: clamp(rect.y + dy, 0, 1 - rect.h),
    w: rect.w,
    h: rect.h,
  };
}

/**
 * Resize from a grabbed edge or corner. Free: move only the grabbed sides. 9:16 lock: resize
 * from the opposite corner, driven by the grabbed axis, keeping the ratio in source pixels.
 */
export function resizeRect(
  rect: CropRect,
  handle: CropHandle,
  p: Point,
  lock916: boolean,
  videoWidth: number,
  videoHeight: number,
): CropRect {
  const n = handle.includes('n');
  const s = handle.includes('s');
  const e = handle.includes('e');
  const w = handle.includes('w');
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;

  if (!lock916) {
    return normalizeRect(
      { x: w ? p.x : rect.x, y: n ? p.y : rect.y },
      { x: e ? p.x : right, y: s ? p.y : bottom },
    );
  }

  const anchor = { x: w ? right : rect.x, y: n ? bottom : rect.y };
  const horizontal = e || w;
  const vertical = n || s;
  const end = {
    x: horizontal ? p.x : w ? rect.x : right,
    y: vertical ? p.y : n ? rect.y : bottom,
  };
  const driver = horizontal && vertical ? 'both' : horizontal ? 'x' : 'y';
  return constrainRectToAspect(anchor, end, videoWidth, videoHeight, PORTRAIT_9_16, driver);
}

export const CROP_CURSOR: Record<Exclude<CropHit, null>, string> = {
  move: 'move',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};
